import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Paper, Typography, Grid, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, TextField, Chip,
  Card, CardContent, Autocomplete, CircularProgress, IconButton,
  Dialog, DialogContent, Radio
} from "@mui/material";
import {
  Search,
  Visibility,
  CheckCircle,
  Cancel,
  Close as CloseIcon,
  FilterAlt,
  DateRange,
  Today,
  CalendarMonth,
  FileDownload
} from "@mui/icons-material";
import { tdsApi } from "../../services/tdsApi";
import ExportButton from "../../components/ExportButton/ExportButton";
import RefreshButton from "../../components/RefreshButton/RefreshButton";
import FormatBadge from "../../components/FormatBadge/FormatBadge";
import { exportAsPDF, exportAsCSV, exportAsExcel } from "../../utils/importExport";
import { showSnackbar } from "../../components/Snackbar";
import AlertModal from "../../components/AlertModal";
import MoreFilters from "../../components/MoreFilters/MoreFilters";


const TDS = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('totalTdsDesc');
  const [tempSortBy, setTempSortBy] = useState('totalTdsDesc');

  // Export Dialog States
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportDateMode, setExportDateMode] = useState('current');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });
  const closeAlert = () => setAlertModal({ open: false, title: 'Alert', message: '' });



  const emptyFilters = {
    name: "",
    deducteeName: "",
    pan: "",
    paymentType: "",
    section: "",
    fromDate: "",
    toDate: "",
    status: "",
  };

  const [filterInputs, setFilterInputs] = useState(emptyFilters);
  const [activeFilters, setActiveFilters] = useState(emptyFilters);

  const [tdsData, setTdsData] = useState([]);
  const [tdsLoading, setTdsLoading] = useState(false);
  const [tdsError, setTdsError] = useState("");

  const paymentTypes = ["Salary", "Professional", "Rent", "Contract", "Commission", "Interest", "Dividend", "Other"];
  const sections = ["194C", "194J", "194I", "195", "192", "193", "194A", "194H"];
  const statuses = ["Deposited", "Pending", "Overdue"];

  useEffect(() => {
    let cancelled = false;

    const fetchReport = async () => {
      setTdsLoading(true);
      try {
        let apiSortBy = 'totalTds';
        let apiSortOrder = 'desc';
        if (sortBy) {
          if (sortBy.endsWith('Desc')) {
            apiSortBy = sortBy.replace('Desc', '');
            apiSortOrder = 'desc';
          } else if (sortBy.endsWith('Asc')) {
            apiSortBy = sortBy.replace('Asc', '');
            apiSortOrder = 'asc';
          }
        }

        const res = await tdsApi.getAssociateTds({
          page: page + 1,
          limit: rowsPerPage,
          startDate: activeFilters.fromDate,
          endDate: activeFilters.toDate,
          name: activeFilters.name,
          sortBy: apiSortBy,
          sortOrder: apiSortOrder,
        });

        const raw = res?.data || [];
        let mapped = raw.map(item => ({
          _id: item._id,
          deducteeName: item.associateName,
          pan: item.panNumber?.[0] || "-",
          amountPaid: item.totalAmount,
          tdsPercentage: item.tdsPercent,
          tdsAmount: item.totalTds,
          dateOfDeduction: "-",
          dateOfDeposit: "-",
          status: "-",
          paymentType: item.paymentType || "-",
          section: item.section || "-"
        }));

        // Frontend filters (Remaining fields that might not be in API yet)
        if (activeFilters.deducteeName) {
          mapped = mapped.filter(i =>
            i.deducteeName.toLowerCase().includes(activeFilters.deducteeName.toLowerCase())
          );
        }
        if (activeFilters.pan) {
          mapped = mapped.filter(i =>
            i.pan.toLowerCase().includes(activeFilters.pan.toLowerCase())
          );
        }
        if (activeFilters.status) {
          mapped = mapped.filter(i => i.status === activeFilters.status);
        }
        if (activeFilters.paymentType) {
          mapped = mapped.filter(i => i.paymentType === activeFilters.paymentType);
        }
        if (activeFilters.section) {
          mapped = mapped.filter(i => i.section === activeFilters.section);
        }

        if (!cancelled) {
          setTdsData(mapped);
          setTotalCount(res?.pagination?.totalRecords || 0);
        }
      } catch (e) {
        if (!cancelled) setTdsError(e.message);
      } finally {
        if (!cancelled) setTdsLoading(false);
      }
    };

    fetchReport();
    return () => { cancelled = true; };
  }, [page, rowsPerPage, activeFilters, sortBy]);

  const handleSearch = () => {
    setActiveFilters(filterInputs);
    setPage(0);
  };

  const handleReset = () => {
    setFilterInputs(emptyFilters);
    setActiveFilters(emptyFilters);
    setSortBy('totalTdsDesc');
    setTempSortBy('totalTdsDesc');
    setPage(0);
  };

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setActiveFilters(filterInputs);
    setPage(0);
    setMoreFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setSortBy('totalTdsDesc');
    setTempSortBy('totalTdsDesc');
    setPage(0);
  };

  const totals = useMemo(() => {
    return tdsData.reduce((acc, curr) => ({
      amountPaid: acc.amountPaid + (Number(curr.amountPaid) || 0),
      tdsAmount: acc.tdsAmount + (Number(curr.tdsAmount) || 0),
    }), { amountPaid: 0, tdsAmount: 0 });
  }, [tdsData]);

  const formatCurrency = a =>
    `Rs. ${Number(a || 0).toLocaleString('en-IN')}`;

  const formatDate = d => d ? new Date(d).toLocaleDateString("en-IN") : "-";

  const buildExportRows = (dataToExport) => {
    const dataSource = dataToExport || tdsData;
    const rows = dataSource.map((r, i) => ({
      "Sr": i + 1,
      "Name": r.deducteeName || "-",
      "PAN": r.pan || "-",
      "Amount": r.amountPaid || 0,
      "TDS%": r.tdsPercentage || 0,
      "TDS": r.tdsAmount || 0,
    }));

    if (dataSource.length > 0) {
      const dataTotals = dataSource.reduce((acc, curr) => ({
        amountPaid: acc.amountPaid + (Number(curr.amountPaid) || 0),
        tdsAmount: acc.tdsAmount + (Number(curr.tdsAmount) || 0),
      }), { amountPaid: 0, tdsAmount: 0 });

      rows.push({
        "Sr": "",
        "Name": "",
        "PAN": "Total:",
        "Amount": dataTotals.amountPaid || 0,
        "TDS%": "-",
        "TDS": dataTotals.tdsAmount || 0,
      });
    }

    return rows;
  };

  const getExportFileName = () => `TDS_Report_${new Date().toLocaleDateString("en-IN").replace(/\//g, "-")}`;

  const handleOpenExportDialog = (type) => {
    setExportType(type);
    setExportDialogOpen(true);
    setExportFromDate(activeFilters.fromDate || '');
    setExportToDate(activeFilters.toDate || '');
    setExportDateMode('current');
  };

  const handleCloseExportDialog = () => {
    setExportDialogOpen(false);
    setExportType('');
    setExportLoading(false);
  };

  const handleConfirmExport = async () => {
    setExportLoading(true);
    try {
      let apiSortBy = 'totalTds';
      let apiSortOrder = 'desc';
      if (sortBy) {
        if (sortBy.endsWith('Desc')) {
          apiSortBy = sortBy.replace('Desc', '');
          apiSortOrder = 'desc';
        } else if (sortBy.endsWith('Asc')) {
          apiSortBy = sortBy.replace('Asc', '');
          apiSortOrder = 'asc';
        }
      }

      const exportParams = {
        export: 'true',
        name: activeFilters.name,
        sortBy: apiSortBy,
        sortOrder: apiSortOrder,
      };

      // Apply filters only if NOT in "All Dates (Full History)" mode
      if (exportDateMode !== 'all') {
        // Date range based on selected mode
        if (exportDateMode === 'current') {
          if (activeFilters.fromDate) exportParams.startDate = activeFilters.fromDate;
          if (activeFilters.toDate) exportParams.endDate = activeFilters.toDate;
        } else if (exportDateMode === 'custom') {
          if (exportFromDate) exportParams.startDate = exportFromDate;
          if (exportToDate) exportParams.endDate = exportToDate;
        } else if (exportDateMode === 'single') {
          if (exportFromDate) {
            exportParams.startDate = exportFromDate;
            exportParams.endDate = exportFromDate;
          }
        }
      }

      const res = await tdsApi.getAssociateTds(exportParams);

      const raw = res?.data || [];
      let mapped = raw.map(item => ({
        _id: item._id,
        deducteeName: item.associateName,
        pan: item.panNumber?.[0] || "-",
        amountPaid: item.totalAmount,
        tdsPercentage: item.tdsPercent,
        tdsAmount: item.totalTds,
        dateOfDeduction: "-",
        dateOfDeposit: "-",
        status: "-",
        paymentType: item.paymentType || "-",
        section: item.section || "-"
      }));

      // Frontend filters matching current logic
      if (activeFilters.deducteeName) {
        mapped = mapped.filter(i =>
          i.deducteeName.toLowerCase().includes(activeFilters.deducteeName.toLowerCase())
        );
      }
      if (activeFilters.pan) {
        mapped = mapped.filter(i =>
          i.pan.toLowerCase().includes(activeFilters.pan.toLowerCase())
        );
      }
      if (activeFilters.status) {
        mapped = mapped.filter(i => i.status === activeFilters.status);
      }
      if (activeFilters.paymentType) {
        mapped = mapped.filter(i => i.paymentType === activeFilters.paymentType);
      }
      if (activeFilters.section) {
        mapped = mapped.filter(i => i.section === activeFilters.section);
      }

      if (mapped.length === 0) {
        setAlertModal({ open: true, title: 'Warning', message: 'No data found for the selected filter/date range.' });
        setExportLoading(false);
        return;
      }

      const rows = buildExportRows(mapped);
      const fileName = getExportFileName();

      if (exportType === 'csv') {
        exportAsCSV({ rows, fileNameBase: fileName });
      } else if (exportType === 'pdf') {
        exportAsPDF({ rows, title: "TDS Report", fileNameBase: fileName });
      } else if (exportType === 'excel') {
        exportAsExcel({ rows, sheetName: "TDS Report", fileNameBase: fileName });
      }

      handleCloseExportDialog();
    } catch (error) {
      console.error('Export failed:', error);
      showSnackbar({ type: 'error', message: 'Export failed. Please try again.' });
    } finally {
      setExportLoading(false);
    }
  };

  const getStatusChip = status => {
    switch (status?.toLowerCase()) {
      case "deposited": return <Chip icon={<CheckCircle />} label="Deposited" color="success" size="small" />;
      case "pending": return <Chip icon={<Cancel />} label="Pending" color="error" size="small" />;
      case "overdue": return <Chip label="Overdue" color="warning" size="small" />;
      default: return <Chip label={status || "Unknown"} size="small" />;
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">TDS Report</Typography>
        <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
          <ExportButton
            onExportPdf={() => handleOpenExportDialog('pdf')}
            onExportCsv={() => handleOpenExportDialog('csv')}
            onExportExcel={() => handleOpenExportDialog('excel')}
          />
          <RefreshButton
            onClick={() => setActiveFilters({ ...activeFilters })}
            loading={tdsLoading}
            sx={{
              fontWeight: 600,
              px: 2,
              fontSize: '15px',
              borderRadius: 2,
              height: { xs: 'auto', sm: 40 }
            }}
          />
        </Box>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">

            <Grid item xs={12} sm={6} md={2}>
              <TextField fullWidth size="small" label="Search Name"
                placeholder="Search by associate name..."
                value={filterInputs.name}
                onChange={e => setFilterInputs(p => ({ ...p, name: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <TextField fullWidth size="small" label="PAN"
                value={filterInputs.pan}
                onChange={e => setFilterInputs(p => ({ ...p, pan: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="From Date"
                InputLabelProps={{ shrink: true }}
                value={filterInputs.fromDate}
                onChange={e => setFilterInputs(p => ({ ...p, fromDate: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={6} md={1.5}>
              <TextField fullWidth size="small" type="date" label="To Date"
                InputLabelProps={{ shrink: true }}
                value={filterInputs.toDate}
                onChange={e => setFilterInputs(p => ({ ...p, toDate: e.target.value }))} />
            </Grid>

            <Grid item xs={12} sm={4} md={1.5}>
              <Button
                variant="contained"
                startIcon={<Search />}
                onClick={handleSearch}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  height: '40px'
                }}
              >
                Search
              </Button>
            </Grid>

            <Grid item xs={12} sm={4} md={1.5}>
              <Button
                variant="outlined"
                onClick={handleReset}
                fullWidth
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: 2,
                  height: '40px'
                }}
              >
                Reset
              </Button>
            </Grid>

            <Grid item xs={12} sm={4} md={2.5}>
              <MoreFilters
                expanded={moreFiltersOpen}
                onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
                sortBy={tempSortBy}
                onChangeSortBy={setTempSortBy}
                onApply={handleApplyFilters}
                onReset={handleResetFilters}
                type="tds"
                height="40px"
              />
            </Grid>

          </Grid>
        </CardContent>
      </Card>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {["Sr", "Name", "PAN", "Amount", "TDS%", "TDS"]
                  .map(h => <TableCell key={h}><b>{h}</b></TableCell>)}
              </TableRow>
            </TableHead>
            <TableBody>
              {tdsLoading ? (
                <TableRow><TableCell colSpan={9} align="center"><CircularProgress /></TableCell></TableRow>
              ) : tdsError ? (
                <TableRow><TableCell colSpan={9} align="center" color="error">{tdsError}</TableCell></TableRow>
              ) : tdsData.length === 0 ? (
                <TableRow><TableCell colSpan={9} align="center">No Data</TableCell></TableRow>
              ) : (
                tdsData.map((r, i) => (
                  <TableRow key={r._id || i}>
                    <TableCell>{page * rowsPerPage + i + 1}</TableCell>
                    <TableCell>{r.deducteeName || "-"}</TableCell>
                    <TableCell>{r.pan || "-"}</TableCell>
                    <TableCell align="">{formatCurrency(r.amountPaid)}</TableCell>
                    <TableCell align="">{r.tdsPercentage ? `${r.tdsPercentage}%` : "-"}</TableCell>
                    <TableCell align="">{formatCurrency(r.tdsAmount)}</TableCell>
                    {/* <TableCell>{formatDate(r.dateOfDeduction)}</TableCell> */}
                    {/* <TableCell>{formatDate(r.dateOfDeposit)}</TableCell> */}
                    {/* <TableCell>{getStatusChip(r.status)}</TableCell> */}
                  </TableRow>
                ))
              )}
              {!tdsLoading && tdsData.length > 0 && (
                <TableRow sx={{ backgroundColor: 'grey.200' }}>
                  <TableCell colSpan={3} align="right"><b>Total:</b></TableCell>
                  <TableCell><b>{formatCurrency(totals.amountPaid)}</b></TableCell>
                  <TableCell>-</TableCell>
                  <TableCell><b>{formatCurrency(totals.tdsAmount)}</b></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, n) => setPage(n)}
          onRowsPerPageChange={e => { setRowsPerPage(+e.target.value); setPage(0); }}
        />
      </Paper>

      {/* Export Date Range Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleCloseExportDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 16px 48px rgba(83, 109, 254, 0.15), 0 2px 12px rgba(0,0,0,0.06)',
          }
        }}
      >
        {/* Gradient Header */}
        <Box sx={{
          background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 50%, #304ffe 100%)',
          px: 2.5, py: 1.8,
          position: 'relative', overflow: 'hidden',
        }}>
          <Box sx={{ position: 'absolute', top: -15, right: -15, width: 55, height: 55, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.08)' }} />
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
              <Box sx={{
                width: 34, height: 34, borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.18)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <FileDownload sx={{ color: '#fff', fontSize: 18 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '0.95rem', lineHeight: 1.2 }}>
                  Export TDS Report
                </Typography>
                <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', mt: 0.2 }}>
                  Download your report data
                </Typography>
              </Box>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small" sx={{ color: 'rgba(255,255,255,0.8)', p: 0.5, '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }}>
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>

        <DialogContent sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
          {/* Format Badge */}
          <FormatBadge type={exportType} />

          {/* Section label */}
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            Select Date Range
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {/* Option 1: Current Filters */}
            <Box
              onClick={() => setExportDateMode('current')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'current' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'current' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'current' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <FilterAlt sx={{ fontSize: 16, color: exportDateMode === 'current' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Current Filters
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {filterInputs.fromDate && filterInputs.toDate
                    ? `${filterInputs.fromDate} → ${filterInputs.toDate}`
                    : filterInputs.fromDate ? `From ${filterInputs.fromDate}` : filterInputs.toDate ? `Till ${filterInputs.toDate}` : 'All dates'}
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'current'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 2: Custom Date Range */}
            <Box
              onClick={() => setExportDateMode('custom')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'custom' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'custom' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'custom' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <DateRange sx={{ fontSize: 16, color: exportDateMode === 'custom' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Custom Date Range
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Choose from & to dates
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'custom'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 3: Single Day */}
            <Box
              onClick={() => setExportDateMode('single')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'single' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'single' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'single' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <Today sx={{ fontSize: 16, color: exportDateMode === 'single' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  Single Day
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export for one specific date
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'single'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>

            {/* Option 4: All Dates (Full History) */}
            <Box
              onClick={() => setExportDateMode('all')}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 1.5, py: 1.2, borderRadius: 2, cursor: 'pointer',
                border: exportDateMode === 'all' ? '1.5px solid #536DFE' : '1.5px solid #e8eaf6',
                bgcolor: exportDateMode === 'all' ? 'rgba(83, 109, 254, 0.04)' : '#fff',
                transition: 'all 0.2s ease',
                '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83, 109, 254, 0.02)' },
              }}
            >
              <Box sx={{
                width: 32, height: 32, borderRadius: 1.5,
                bgcolor: exportDateMode === 'all' ? '#536DFE' : '#e8eaf6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s ease', flexShrink: 0,
              }}>
                <CalendarMonth sx={{ fontSize: 16, color: exportDateMode === 'all' ? '#fff' : '#536DFE' }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#1a1a2e' }}>
                  All Data (Full History)
                </Typography>
                <Typography sx={{ fontSize: '0.68rem', color: '#999', mt: 0.1 }}>
                  Export full database history without any filters
                </Typography>
              </Box>
              <Radio checked={exportDateMode === 'all'} size="small" sx={{ p: 0.3, color: '#c5cae9', '&.Mui-checked': { color: '#536DFE' } }} />
            </Box>
          </Box>

          {/* Date inputs — compact animated */}
          {exportDateMode === 'custom' && (
            <Box sx={{
              display: 'flex', gap: 1.5, mt: 1.5,
              p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="From Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
              <TextField
                label="To Date" type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}

          {exportDateMode === 'single' && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 2,
              bgcolor: '#fafbff', border: '1px solid #e8eaf6',
              animation: 'fadeIn 0.2s ease',
              '@keyframes fadeIn': { '0%': { opacity: 0, transform: 'translateY(-4px)' }, '100%': { opacity: 1, transform: 'translateY(0)' } },
            }}>
              <TextField
                label="Select Date" type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: '#fff', fontSize: '0.82rem' }, '& .MuiInputLabel-root': { fontSize: '0.78rem' } }}
              />
            </Box>
          )}
        </DialogContent>

        {/* Actions */}
        <Box sx={{ px: 2.5, py: 1.5, bgcolor: '#fafbff', borderTop: '1px solid #eef2f6', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button
            onClick={handleCloseExportDialog}
            variant="outlined" size="small"
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 600,
              px: 2, fontSize: '0.8rem', borderColor: '#c5cae9', color: '#536DFE',
              '&:hover': { borderColor: '#536DFE', bgcolor: 'rgba(83,109,254,0.04)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmExport}
            variant="contained" size="small"
            disabled={exportLoading || (exportDateMode === 'custom' && (!exportFromDate || !exportToDate)) || (exportDateMode === 'single' && !exportFromDate)}
            startIcon={exportLoading ? <CircularProgress size={14} color="inherit" /> : <FileDownload sx={{ fontSize: 16 }} />}
            sx={{
              borderRadius: 2, textTransform: 'none', fontWeight: 700,
              px: 2.5, fontSize: '0.8rem',
              background: 'linear-gradient(135deg, #536DFE 0%, #3d5afe 100%)',
              boxShadow: '0 3px 10px rgba(83, 109, 254, 0.3)',
              '&:hover': { background: 'linear-gradient(135deg, #3d5afe 0%, #304ffe 100%)', boxShadow: '0 4px 14px rgba(83, 109, 254, 0.4)' },
              '&.Mui-disabled': { background: '#e0e0e0', boxShadow: 'none' },
            }}
          >
            {exportLoading ? 'Exporting...' : 'Export Now'}
          </Button>
        </Box>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default TDS;
